import { get, isEmpty } from 'lodash';
import {
  observable, action, toJS, computed,
} from 'mobx';
import {
  DocItem, DocType,
  ReqStatus, DocStatus,
  NewDocBodyData, DocTreeQuery,
} from '../Types';
import {
  getDocPlatformTree, deleteDoc,
  getDocContext, createNewDoc,
  updateDocInfo, docRelease,
  docOnline, docOffline,
  updateDocTree, previewAPIContent,
  updateMultiDocInfo,
} from '../server/documentServer';
import {
  updateDocById, getNodeLevel,
  getParentDocId, isExpandTree,
  deleteDocById, addDocByLevel,
} from '@/pages/document/utils';
import errCode from '@/utils/request/errCode';
import i18next from 'i18next';
import Message from '@/component/Message';

class DocumentStore {
  @observable version = 'commonStore-1.0.0';

  //  文档列表
  @observable documentList: DocItem[] = [];

  // 文档列表副本 用来搜索时 保存原列表
  @observable defaultDocumentList: DocItem[] = [];

  // 文档排序后的列表
  @observable docSortListMap = {};

  //  文档的版本
  @observable documentVersion = 0;

  // 文档关系映射表
  @observable documentListMap: any = {};

  // 当前menu请求的状态
  @observable getMenuStatus: ReqStatus = 'Loading';

  // 当前文档库的名称
  @observable currentDocName = '';

  // 当前文档对外的 identify_key
  @observable identifyKey = '';

  // 当前文档的默认语言
  @observable defaultDocLang = 'CHINESE';

  // 当前文档的语言
  @observable activeDocLang = '';

  // 当前文档库的多语言列表
  @observable docLanguageList = ['CHINESE'];

  // 当前文档库是不是多语言文档
  @observable isMultiLanguage = false;

  // 当前是否是可以编辑的文档
  @computed get isMultiEditDoc() {
    return this.activeDocLang === this.defaultDocLang;
  }

  // 当前活跃的docId
  @observable activeDocId = 0;

  // 当前操作的文档类型
  @observable activeDocType: DocType = 'MARKDOWN';

  // 当前文档的草稿内容
  @observable activeDocDraftContent = '';

  // 当前文档内容的副本 用来编辑文档内容时使用
  @observable activeDocEditContent = '';

  // 当前文档的 API ID
  @observable activeDocApiId = 0;

  // 当前文档的 APIID 的副本  用来编辑 API 文档时候使用
  @observable activeDocEditApiId = 0;

  // 当前API 文档的serviceId
  @observable activeApiServiceId = 0;

  // 当前API 文档的 serviceID 用来编辑 API 文档时候使用
  @observable activeApiEditServiceId = 0;

  // 当前API 文档的 teamId
  @observable activeApiTeamId = 0;

  // 当前API文档的 teamId 用来编辑 API 文档时候使用
  @observable activeApiEditTeamId = 0;

  // 当前文档的请求状态
  @observable getDocStatus: ReqStatus = 'Loading';

  // 当前文档的上线状态
  @observable activeDocStatus: DocStatus = false;

  // 多语言文档修改的内容
  @observable multiDocChangeContent: any = {};

  // 当前文档的title
  @observable activeDocTitle = '';

  // 当前文档的title副本 用来编辑文档标题是使用
  @observable activeDocEditTitle = '';

  // 多语言原文的标题
  @observable originTitle = '';

  // 文档是否是可编辑状态
  @observable isEdit = false;

  // 文档列表是否是排序状
  @observable isSort = false;

  // 文档内容是否发生变化
  @observable docHasEdit = false;

  @action
  storeReset() {
    this.documentList = [];
    this.defaultDocumentList = [];
    this.docSortListMap = {};
    this.documentVersion = 0;
    this.documentListMap = {};
    this.getMenuStatus = 'Loading';
    this.defaultDocLang = 'CHINESE';
    this.activeDocLang = '';
    this.docLanguageList = ['CHINESE'];
    this.isMultiLanguage = false;
    this.currentDocName = '';
    this.activeDocId = 0;
    this.activeDocType = 'MARKDOWN';
    this.activeDocDraftContent = '';
    this.activeDocEditContent = '';
    this.activeDocApiId = 0;
    this.activeDocEditApiId = 0;
    this.activeApiServiceId = 0;
    this.activeApiTeamId = 0;
    this.activeApiEditServiceId = 0;
    this.activeApiEditTeamId = 0;
    this.getDocStatus = 'Loading';
    this.activeDocStatus = false;
    this.originTitle = '';
    this.activeDocTitle = '';
    this.activeDocEditTitle = '';
    this.multiDocChangeContent = null;
    this.isEdit = false;
    this.isSort = false;
    this.docHasEdit = false;
  }

  @action
  async getDocPlatformTree(param: DocTreeQuery) {
    this.getMenuStatus = 'Loading';
    // 更新列表树时 同时更新 doc 内容   ---  暂定
    this.getDocStatus = 'Loading';
    this.documentList = [];
    this.defaultDocumentList = [];
    const result = await getDocPlatformTree({ language: this.activeDocLang, ...param });
    this.getMenuStatus = 'Success';

    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_tree_get_error'));
      return;
    }

    const list = get(result, 'data.primaryDocList', []);
    const lang = get(result, 'data.mainLanguage', 'CHINESE');
    const languageList = get(result, 'data.languageList', ['CHINESE']);
    this.documentVersion = get(result, 'data.version', 0);
    this.documentListMap = get(result, 'data.docTreeMap', {});
    this.currentDocName = get(result, 'data.docPlatformName', '');
    this.identifyKey = get(result, 'data.identifyKey', '');
    this.isMultiLanguage = get(result, 'data.isMultiLanguage', false);
    this.activeDocLang = this.activeDocLang || lang; // 如果是url上带了语言则设置url上的语言为活跃语言
    this.defaultDocLang = lang;
    const listMap = languageList.filter((item: string) => lang !== item);
    listMap.unshift(lang);
    this.docLanguageList = listMap;
    this.defaultDocumentList = list;

    // 当前无活跃状态的文档时， 设置文档树第一个节点为活跃状态的文档
    // url上的 doc_id 如果在 list 中找不到 也将第一个节点设置为活跃文档
    const level = getNodeLevel(list, this.activeDocId);
    if (!level) {
      this.activeDocId = get(result, 'data.primaryDocList[0].docId', 0);
      this.documentList = list;
    } else {
      this.documentList = isExpandTree(list, level, false);
    }
    this.getDocContext();

    this.getMenuStatus = 'Success';
  }

  @action
  async getDocContext() {
    this.getDocStatus = 'Loading';
    // 活跃文档id为0时 直接无结果
    if (this.activeDocId === 0) {
      this.getDocStatus = 'None';
      return;
    }
    const result = await getDocContext({ docId: this.activeDocId, language: this.activeDocLang });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_content_get_error'));
      return;
    }
    this.activeDocApiId = get(result, 'data.apiId', 0);
    this.activeDocEditApiId = get(result, 'data.apiId', 0);
    this.activeApiServiceId = get(result, 'data.serviceId', 0);
    this.activeApiEditServiceId = get(result, 'data.serviceId', 0);
    this.activeApiEditTeamId = get(result, 'data.teamId', 0);
    this.activeApiTeamId = get(result, 'data.teamId', 0);
    this.activeDocStatus = get(result, 'data.status', false);
    this.activeDocDraftContent = get(result, 'data.draftContent', '');
    this.activeDocEditContent = get(result, 'data.draftContent', '');
    this.activeDocTitle = get(result, 'data.title', '');
    this.activeDocEditTitle = get(result, 'data.title', '');
    this.activeDocType = get(result, 'data.type', '');
    this.originTitle = get(result, 'data.originTitle', '');
    this.getDocStatus = 'Success';
  }

  @action
  async createNewDoc(data: NewDocBodyData, isChild: boolean, docId: any) {
    const newId = -1; // 用来占位
    const origin = toJS(this.defaultDocumentList); // documentList 有可能是不完整的 所以用 defaultDocumentList
    const level = docId === 'new' ? 'new' : getNodeLevel(origin, docId); // 获取当前操作文档的层级
    const { parentId, index } = getParentDocId(origin, level, isChild);
    let listmap = toJS(this.documentListMap);

    if (isEmpty(listmap)) {
      listmap = { 0: { childIds: [], parentId: -1, status: 1 } };
    }
    const listmapParent = listmap[parentId];
    const listmapChilds = listmap[parentId].childIds;
    listmapChilds.splice(index, 0, newId);
    listmap[parentId] = { ...listmapParent, childIds: listmapChilds };

    const result = await createNewDoc({
      title: this.activeDocTitle,
      ...data,
      docTreeMap: listmap,
      content: '',
      version: this.documentVersion,
    });

    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_create_get_error'));
      return Promise.resolve();
    }

    this.documentVersion = get(result, 'data.docTreeData.version', 0);
    this.documentListMap = get(result, 'data.docTreeData.docTreeMap', {});
    this.activeDocId = get(result, 'data.docId', 0);
    this.getMenuStatus = 'Success';
    this.isEdit = true;
    const list = addDocByLevel(origin, level, isChild, {
      docId: this.activeDocId,
      title: data.title,
      type: data.docType,
      isModify: false,
      parentId,
      status: true,
      childDocs: [],
    });
    // 创建子节点 需要展开父节点
    if (isChild) {
      this.documentList = isExpandTree(list, level, false);
    } else {
      this.documentList = list;
    }
    this.defaultDocumentList = toJS(this.documentList); // 同步到备份的 documentList
    this.getDocContext();

    return Promise.resolve();
  }

  @action
  async updateDocInfo() {
    const result = await updateDocInfo({
      language: this.activeDocLang,
      docId: this.activeDocId,
      apiId: this.activeDocEditApiId,
      content: this.activeDocEditContent,
      title: this.activeDocEditTitle || (this.activeDocType === 'API' ? i18next.t('chose_interface') : i18next.t('edit_doc_title')),
    });
    // 如果是修改API文档 不让重修改
    if (result.code === 40000) {
      this.isEdit = false;
      this.docHasEdit = false;
      return Promise.resolve('success');
    }
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_update_content_error'));
      return Promise.resolve('success');
    }
    this.isEdit = false;
    this.docHasEdit = false;
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    const data = get(result, 'data', {});
    this.activeDocApiId = get(result, 'data.appId', 0);
    this.activeDocEditApiId = get(result, 'data.appId', 0);
    this.activeDocDraftContent = get(result, 'data.content', '');
    this.activeDocEditContent = get(result, 'data.content', '');
    this.activeDocTitle = get(result, 'data.title', '');
    this.activeDocEditTitle = get(result, 'data.title', '');
    this.documentList = updateDocById(list, this.activeDocId, { ...data });
    this.defaultDocumentList = updateDocById(defaultList, this.activeDocId, { ...data });
    Message.success(i18next.t('doc_update_content_success'));

    Promise.resolve('success');
  }

  @action
  async docRelease(docId: number) {
    const result = await docRelease({
      docId,
      language: this.activeDocLang,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_update_status_release_error'));
      return;
    }
    if (result.code === 40000) {
      Message.error(i18next.t('set_doc_content'));
      return;
    }
    if (result.code === 40001) {
      Message.error(i18next.t('check_translate_doc'));
      return;
    }
    const isModify = get(result, 'data.isModify', true);
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    this.defaultDocumentList = updateDocById(defaultList, docId, { isModify });
    this.documentList = updateDocById(list, docId, { isModify });
    Message.success(i18next.t('doc_update_status_releass_success'));
  }

  @action
  async docOnline(docId: number) {
    const result = await docOnline({
      docId,
      language: this.activeDocLang,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_update_status_error'));
      return;
    }
    const isModify = get(result, 'data.isModify', false);
    this.documentVersion = get(result, 'data.version', this.documentVersion);
    this.activeDocStatus = true;
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    this.defaultDocumentList = updateDocById(defaultList, docId, { status: true, isModify });
    this.documentList = updateDocById(list, docId, { status: true, isModify });

    Message.success(i18next.t('doc_update_status_success'));
  }

  @action
  async docOffline(docId: number) {
    const result = await docOffline({
      docId,
      language: this.activeDocLang,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_update_status_error'));
      return;
    }
    const isModify = get(result, 'data.isModify', false);
    this.documentVersion = get(result, 'data.version', this.documentVersion);
    this.activeDocStatus = false;
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    this.defaultDocumentList = updateDocById(defaultList, docId, { status: false, isModify });
    this.documentList = updateDocById(list, docId, { status: false, isModify });

    Message.success(i18next.t('doc_update_status_success'));
  }

  @action
  async deleteDoc(level: string, docId: number) {
    const result = await deleteDoc({ docId });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_delete_error'));
      return Promise.resolve('error');
    }
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    const { list: newList, id } = deleteDocById(list, docId, this.activeDocId);
    const { list: newDefaultList } = deleteDocById(defaultList, docId, this.activeDocId);
    this.documentList = newList;
    this.defaultDocumentList = newDefaultList;
    this.documentVersion = get(result, 'data.docTreeData.version', 0);
    this.documentListMap = get(result, 'data.docTreeData.docTreeMap', {});
    this.getMenuStatus = 'Success';
    // 如果删除的节点就是当前活跃的节点 或者活跃文档是删除文档的子文档 我们就需要重置 activeDocId 获取当前活跃文档的内容
    if (this.activeDocId !== id) {
      this.activeDocId = id;
      this.getDocContext();
    }
    Message.success(i18next.t('doc_delete_success'));
    return Promise.resolve('success');
  }

  @action
  async updateDocTree(docPlatformId: number) {
    const listTree = toJS(this.docSortListMap);
    // 如果直接点击确定的话直接返回
    if (isEmpty(listTree)) {
      return Promise.resolve('error');
    }
    const result = await updateDocTree({
      docPlatformId,
      docTreeMap: listTree,
      version: this.documentVersion,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('doc_sort_error'));
      return Promise.resolve('success');
    }
    this.documentList = get(result, 'data.primaryDocList', []);
    this.defaultDocumentList = get(result, 'data.primaryDocList', []);
    this.documentVersion = get(result, 'data.version', 0);
    this.documentListMap = get(result, 'data.docTreeMap', {});
    this.currentDocName = get(result, 'data.docPlatformName', '');

    return Promise.resolve('success');
  }

  @action
  async previewAPIContent() {
    const result = await previewAPIContent({
      apiId: this.activeDocEditApiId,
      language: this.defaultDocLang,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('api_preview_error'));
      return;
    }
    this.activeDocEditContent = get(result, 'data.mdContent', '');
  }

  @action
  async updateMultiDocInfo() {
    const translationMap = JSON.stringify(toJS(this.multiDocChangeContent));
    const result = await updateMultiDocInfo({
      docId: this.activeDocId,
      language: this.activeDocLang,
      translationMap,
    });
    if (result.code !== errCode.SUCCESS) {
      Message.error(i18next.t('api_mutli_doc_error'));
      return Promise.resolve('error');
    }
    this.multiDocChangeContent = {};
    this.isEdit = false;
    this.docHasEdit = false;
    this.getDocContext();
    const list = toJS(this.documentList);
    const defaultList = toJS(this.defaultDocumentList);
    this.activeDocEditTitle = get(result, 'data.title', '');
    this.documentList = updateDocById(list, this.activeDocId, { title: this.activeDocEditTitle, isModify: result.data.isModify });
    this.defaultDocumentList = updateDocById(defaultList, this.activeDocId, { title: this.activeDocEditTitle, isModify: result.data.isModify });
    Message.success(i18next.t('doc_update_content_success'));

    return Promise.resolve('success');
  }
}

export default { name: 'documentStore', Store: DocumentStore };
